import { useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { UploadCloud } from 'lucide-react';
import WorkPreviewCard from './WorkPreviewCard';
import { detectFileType } from '@/lib/intakeUpload';

/**
 * WorkDropzone — multi-file picker with drag/drop.
 *
 * Maintains an array of work entries. Parent (ArtistIntakePage) owns the state
 * via `entries` + `onChange(entries)` so it can drive uploads + final submit.
 *
 * Each entry shape:
 *   { id, file, fileType, title?, description?, status, progress, objectUrl,
 *     dimensions?, duration?, storage_path?, file_url?, thumbnail_path?, thumbnail_url?, error? }
 */
function nanoId() {
  return Math.random().toString(36).slice(2, 11) + Date.now().toString(36);
}

export default function WorkDropzone({ entries, onChange, disabled }) {
  const inputRef = useRef(null);
  const [isOver, setIsOver] = useState(false);

  const addFiles = (files) => {
    if (!files?.length) return;
    const newEntries = Array.from(files).map((f) => ({
      id: nanoId(),
      file: f,
      fileType: detectFileType(f),
      title: '',
      description: '',
      status: 'queued',
      progress: 0,
      objectUrl: f.type?.startsWith('image/') || f.type?.startsWith('video/') ? URL.createObjectURL(f) : null,
    }));
    onChange([...entries, ...newEntries]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    if (disabled) return;
    addFiles(e.dataTransfer.files);
  };

  const removeEntry = (id) => {
    const target = entries.find((x) => x.id === id);
    if (target?.objectUrl) {
      try { URL.revokeObjectURL(target.objectUrl); } catch (e) { /* noop */ }
    }
    onChange(entries.filter((x) => x.id !== id));
  };

  const updateEntry = (id, patch) => {
    onChange(entries.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  return (
    <div className="space-y-4" data-testid="work-dropzone">
      {/* Drop area */}
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsOver(true); }}
        onDragLeave={() => setIsOver(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`border ${isOver ? 'border-[#90E0EF]/60 bg-[#90E0EF]/5' : 'border-dashed border-[#333]'} bg-[#0a0a0a] transition-colors py-10 px-6 text-center`}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        data-testid="work-drop-area"
      >
        <UploadCloud className={`w-7 h-7 mx-auto mb-3 ${isOver ? 'text-[#90E0EF]' : 'text-[#555]'}`} />
        <p className="font-outfit text-base font-medium text-white">
          Drop files here or click to upload
        </p>
        <p className="text-[11px] font-mono text-[#888] mt-1.5 tracking-[0.05em]">
          Images, videos, PDFs. Files upload directly to the gallery storage.
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*,application/pdf"
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
      />

      {/* List */}
      {entries.length > 0 && (
        <div className="space-y-3">
          <AnimatePresence initial={false}>
            {entries.map((entry) => (
              <WorkPreviewCard
                key={entry.id}
                entry={entry}
                onRemove={removeEntry}
                onChange={updateEntry}
                disabled={disabled}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
